// --- QualifierService.ts ---
import { ConfigurationRepository } from '../configuration/ConfigurationRepository';
import { QualifierRepository } from './QualifierRepository';
import { AppOptions } from '../../../application/options/AppOptions';
import { CookiesStorageService } from '@Engage.Data/services';
import { StorageKeys } from '@Engage.Data/constants/Constants';
/**
 * Service to fetch and cache qualifiers
 */
export class QualifierService {
    /**
     * Check if cookie exists (valid)
     */
    isCookieValid(cookieName) {
        const cookie = CookiesStorageService.get(cookieName, { isSecureValue: true });
        return !!cookie;
    }
    /**
     * Get qualifiers with caching logic
     */
    async getQualifiers() {
        var _a, _b, _c;
        const siteCode = AppOptions.getSiteCode();
        const configCode = AppOptions.getConfigCode();
        const resourceUrl = AppOptions.getResourceUrl();
        const qualifierKey = `${StorageKeys.Qualifiers}__${siteCode}__${configCode}`;
        try {
            // Always fetch publish settings to check freshness.
            // Fetch qualifiers in parallel — only if cache cookie is missing.
            const cacheExists = this.isCookieValid(qualifierKey);
            const [apiPublishSettings, freshQualifiers] = await Promise.all([
                ConfigurationRepository.getPublishSettingsFromApi(resourceUrl, siteCode, configCode),
                cacheExists ? Promise.resolve(null) : QualifierRepository.getQualifierList()
            ]);
            const apiDate = new Date(apiPublishSettings.Date).getTime();
            const cookiePublish = ConfigurationRepository.getPublishSettingsFromCookie();
            const cookieDate = cookiePublish
                ? new Date((_a = cookiePublish.publishSettings) === null || _a === void 0 ? void 0 : _a.Date).getTime()
                : null;
            // Cache hit — dates match, same config, and cookie still alive
            const isSameConfig = ((_b = cookiePublish === null || cookiePublish === void 0 ? void 0 : cookiePublish.publishSettings) === null || _b === void 0 ? void 0 : _b.ConfigCode) === configCode
                && ((_c = cookiePublish === null || cookiePublish === void 0 ? void 0 : cookiePublish.publishSettings) === null || _c === void 0 ? void 0 : _c.SiteCode) === siteCode;
            if (cookieDate === apiDate && cacheExists && isSameConfig) {
                return JSON.parse(CookiesStorageService.get(qualifierKey, { isSecureValue: true }));
            }
            // Cache miss — use already-fetched qualifiers or fetch now
            const qualifiers = freshQualifiers !== null && freshQualifiers !== void 0 ? freshQualifiers : await QualifierRepository.getQualifierList();
            // Persist synchronously — no need to await storage writes
            CookiesStorageService.set(qualifierKey, JSON.stringify(qualifiers), {
                isSecureValue: true,
                daysOfLife: 1
            });
            ConfigurationRepository.setPublishSettings({
                publishSettings: { ...apiPublishSettings, ConfigCode: configCode, SiteCode: siteCode }
            });
            return qualifiers;
        }
        catch (err) {
            console.error('[QualifierService] Error fetching qualifiers:', err);
            throw err;
        }
    }
}
